/**
 * @module components/Form/FormGroupGeolocation
 * @author Charles Blais <charles.blais@canada.ca>
 *
 * Button for getting current user location using Bootstrap 3
 */

import { useEffect } from 'react';
import { Col, ControlLabel, FormGroup, FormControl, Button, HelpBlock, Panel } from 'react-bootstrap';

import { useLanguageState } from '../../contexts/language-state-provider';

import { useAppState } from '../../contexts/app-state-provider';

import useGeolocationService from '../../hooks/use-geolocation-service';


type Props = {
  address: string;
  onCoordinates: FormOnCoordinates;
  onAddress: (v: string) => void;
}


/**
 * React component
 * ===============
 */
const FormGroupGeolocation = ({
  address,
  onCoordinates,
  onAddress,
}: Props) => {
  const [lang] = useLanguageState();
  const [state] = useAppState();

  const { data, loading, error, setSearch } = useGeolocationService({
    initialSearch: address,
    url: state.geolocationService
  });

  // Only return the location if the state has changed to the callback routine
  // TODO -  onCoordinates always get regenerated so need to find a way to prevent it
  useEffect(() => {
    if (data.length) {
      onCoordinates(data[0].coordinates.latitude, data[0].coordinates.longitude);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      <FormGroup controlId="address" validationState={(error) ? 'error' : null}>
        <Col componentClass={ControlLabel} sm={2}>
          {lang.form.address}
        </Col>
        <Col sm={10}>
          <FormControl
            type="text"
            value={address}
            onChange={(e) => onAddress((e.target as HTMLButtonElement).value)}
            placeholder={lang.form.address}
            style={{ width: '100%' }}
          />
          <FormControl.Feedback />
          <HelpBlock>{lang.form.coordinatesAddressHelp}</HelpBlock>
          {error &&
            <Panel bsStyle="danger">
              <Panel.Body>{lang.form.error.location}</Panel.Body>
            </Panel>
          }
        </Col>
      </FormGroup>
      <Col smOffset={2}>
        <Button
          onClick={() => setSearch(address)}
          disabled={loading}
        >
          {lang.form.searchAddress}
        </Button>
      </Col>
    </>
  );
}

export default FormGroupGeolocation;
